/**
 * Auth token store.
 *
 * Go's JWTMiddleware (`internal/auth/jwt_middleware.go::bearerToken`)
 * reads from the `Authorization: Bearer <token>` header only — it
 * deliberately ignores the `antifraud_access_token` cookie the auth
 * handler also sets. So even with `credentials: 'include'` from the
 * browser, the API would 401 because the cookie carve-out lives in
 * the auth-handler layer, not in the middleware that gates every
 * other endpoint.
 *
 * This module is the React-side fix: keep the access + refresh
 * tokens client-side and let `api/client.ts` attach the bearer
 * header on every request.
 *
 * Storage strategy:
 *
 *   - In-memory is the source of truth (a module-level variable).
 *     XSS that reaches our JS can read it, but so can it read any
 *     cookie or storage key — there's no perfectly XSS-safe
 *     transport for SPAs that talk to a header-only API.
 *
 *   - localStorage is the durable mirror. Page reload, deep-link
 *     paste, and right-click → "Open in new tab" all inherit the
 *     analyst's session — `sessionStorage` is per-tab and breaks
 *     the new-tab path. The shared-workstation safety property
 *     comes from the inactivity timeout in `useSessionTimer.ts`,
 *     which clears tokens after the configured idle window.
 *
 *   - The `storage` event from the browser fires across tabs of
 *     the same origin whenever localStorage changes, so logging
 *     out in one tab clears the in-memory copy in every other
 *     open tab automatically.
 */

const STORAGE_KEY = 'antifraud.auth.tokens.v1'
const CHANGE_EVENT = 'antifraud:auth-tokens-changed'
// Mirror of useSessionTimer's STORAGE_KEY. Inlined here (rather
// than imported) to avoid a circular dependency between auth-tokens
// and the session-timer hook. Both modules must agree on the value.
//
// AF-402 bug fix: setTokens() writes a fresh value, clearTokens()
// removes the key. Without this, an inactivity-driven logout left
// a stale last_activity in localStorage; the next login mounted
// useSessionTimer with an immediately-expired window, triggering a
// second logout. The user had to log in twice for the session to
// stick. Keeping the two keys in lockstep makes the first login
// always start a fresh idle window.
const SESSION_ACTIVITY_KEY = 'antifraud.session.last_activity.v1'

export interface Tokens {
  access_token: string
  refresh_token: string
  /** Wall-clock seconds until the access token expires (server-issued). */
  expires_in: number
  /** Captured locally at set-time so we can refresh proactively. */
  issued_at: number
}

let memo: Tokens | null = readFromStorage()

if (typeof window !== 'undefined') {
  // Cross-tab sync: when localStorage changes in another tab,
  // refresh our in-memory copy so the two tabs don't drift.
  window.addEventListener('storage', (e) => {
    if (e.storageArea !== window.localStorage) return
    if (e.key !== STORAGE_KEY) return
    memo = readFromStorage()
    emitChange()
  })
}

export function getTokens(): Tokens | null {
  return memo
}

export function setTokens(tokens: Omit<Tokens, 'issued_at'>): void {
  const next: Tokens = { ...tokens, issued_at: Math.floor(Date.now() / 1000) }
  memo = next
  writeToStorage(next)
  // Bump the inactivity-timer's activity key to "now" so the
  // freshly-logged-in session doesn't inherit a stale timestamp
  // from a prior logged-out window. See SESSION_ACTIVITY_KEY note.
  writeSessionActivity(Date.now())
  emitChange()
}

export function clearTokens(): void {
  memo = null
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY)
    // Best-effort cleanup of the legacy sessionStorage key so users
    // who already had a session under the old strategy don't end up
    // with stale tokens trapped in a per-tab store.
    window.sessionStorage.removeItem(STORAGE_KEY)
    // Clear the activity timestamp too so the next login starts a
    // fresh idle window (otherwise useSessionTimer reads the stale
    // pre-logout timestamp and immediately triggers a second logout).
    window.localStorage.removeItem(SESSION_ACTIVITY_KEY)
  }
  emitChange()
}

/** Subscribe to local token changes (login / logout / refresh). */
export function onTokensChanged(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(CHANGE_EVENT, handler)
  return () => window.removeEventListener(CHANGE_EVENT, handler)
}

/** True iff the access token is past or within `skewSeconds` of expiry. */
export function accessTokenExpired(skewSeconds = 30): boolean {
  if (!memo) return true
  const now = Math.floor(Date.now() / 1000)
  return now >= memo.issued_at + memo.expires_in - skewSeconds
}

function readFromStorage(): Tokens | null {
  if (typeof window === 'undefined') return null
  try {
    // Read localStorage first (current strategy); fall back to the
    // legacy sessionStorage key so any in-flight session created
    // before this change survives the upgrade.
    const raw =
      window.localStorage.getItem(STORAGE_KEY) ??
      window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Tokens
    if (
      typeof parsed.access_token !== 'string' ||
      typeof parsed.refresh_token !== 'string' ||
      typeof parsed.expires_in !== 'number' ||
      typeof parsed.issued_at !== 'number'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeToStorage(tokens: Tokens): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
  } catch {
    // Quota / private-mode: in-memory store is still authoritative,
    // so we just lose the page-reload survivability.
  }
}

function writeSessionActivity(ts: number): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SESSION_ACTIVITY_KEY, String(ts))
  } catch {
    // Quota / private-mode: best-effort. The session-timer's first
    // mousemove will write a fresh value too.
  }
}

function emitChange(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

// Test-only — vitest can call this between tests so the module-level
// memo doesn't bleed across cases.
export function __resetAuthTokensForTests(): void {
  memo = null
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY)
    window.sessionStorage.removeItem(STORAGE_KEY)
    window.localStorage.removeItem(SESSION_ACTIVITY_KEY)
  }
}
